import React, { useState, useEffect } from 'react';
import api from '../api';
import './Reports.css';

export default function Reports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Date range filters (default to last 30 days)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/reports', {
        params: { startDate, endDate }
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      setError('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  const handleApplyFilter = (e) => {
    e.preventDefault();
    fetchReportData();
  };

  const handleExportPDF = () => {
    window.print();
  };

  // Helper to format seconds into mm:ss
  const formatDuration = (seconds) => {
    if (!seconds) return '0m 0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  // Helper to get max value for chart scaling
  const getChartMax = (callVolume) => {
    if (!callVolume || callVolume.length === 0) return 10;
    const maxVal = Math.max(...callVolume.map(item => item.value));
    return maxVal < 10 ? 10 : maxVal; // Minimum scale of 10
  };

  return (
    <div className="reports-page">
      <div className="reports-header no-print">
        <div className="reports-header-text">
          <h1>Analytics &amp; Reports</h1>
          <p>Track your workspace performance, lead conversions, and agent call volumes.</p>
        </div>
        <div className="reports-actions">
          <button className="btn-export btn-export-secondary" onClick={() => alert('CSV Export coming soon!')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Export CSV
          </button>
          <button 
            className="btn-export btn-export-primary"
            onClick={handleExportPDF}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
            Download Full Report (PDF)
          </button>
        </div>
      </div>

      <div className="reports-filter-bar no-print">
        <form onSubmit={handleApplyFilter} className="date-filter-form">
          <div className="filter-group">
            <label>Start Date</label>
            <input 
              type="date" 
              value={startDate} 
              onChange={e => setStartDate(e.target.value)} 
              required 
            />
          </div>
          <div className="filter-group">
            <label>End Date</label>
            <input 
              type="date" 
              value={endDate} 
              onChange={e => setEndDate(e.target.value)} 
              required 
            />
          </div>
          <button type="submit" className="btn-filter-apply">Apply Filter</button>
        </form>
      </div>

      {/* Print-only title */}
      <div className="print-only print-header">
        <h1>Scaalable CRM - Analytics Report</h1>
        <p>Report Period: {startDate} to {endDate}</p>
      </div>

      {loading && <div className="reports-loading">Loading report data...</div>}
      {error && <div className="reports-error">{error}</div>}

      {!loading && data && (
        <>
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="kpi-title">Total Leads Processed</div>
              <div className="kpi-value">{data.totalLeads.toLocaleString()}</div>
            </div>
            
            <div className="kpi-card">
              <div className="kpi-title">Lead Conversion Rate</div>
              <div className="kpi-value">{data.conversionRate}%</div>
              <div className="kpi-subtitle">{data.convertedLeads.toLocaleString()} Converted Leads</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-title">Total Connected Calls</div>
              <div className="kpi-value">{data.connectedCalls.toLocaleString()}</div>
              <div className="kpi-subtitle">Out of {data.totalCalls.toLocaleString()} Total Calls</div>
            </div>

            <div className="kpi-card">
              <div className="kpi-title">Avg Call Duration</div>
              <div className="kpi-value">{formatDuration(data.avgCallDurationSeconds)}</div>
            </div>
          </div>

          <div className="charts-grid">
            {/* Main Chart Card */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Call Volume Over Time</h3>
              </div>
              <div className="css-bar-chart">
                {data.callVolumeByDate && data.callVolumeByDate.length > 0 ? (
                  data.callVolumeByDate.map((item, index) => {
                    const maxScale = getChartMax(data.callVolumeByDate);
                    const heightPercent = (item.value / maxScale) * 100;
                    
                    // Format date to short string (e.g. "Sep 15")
                    const dateObj = new Date(item.date);
                    const dateStr = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

                    return (
                      <div key={index} className="chart-bar-group">
                        <div className="chart-tooltip">{item.value} Calls on {dateStr}</div>
                        <div className="chart-bar" style={{ height: `${heightPercent}%` }}></div>
                        <div className="chart-label">{dateStr}</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-chart">No call data available for this date range.</div>
                )}
              </div>
            </div>

            {/* Progress List Card */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Agent Performance (Converted Leads)</h3>
              </div>
              <div className="progress-list">
                {data.agentPerformance && data.agentPerformance.length > 0 ? (
                  data.agentPerformance.map((agent, index) => {
                    // Calculate max calls for progress bar scaling
                    const maxConversions = Math.max(...data.agentPerformance.map(a => a.calls));
                    const progressPercent = maxConversions > 0 ? (agent.calls / maxConversions) * 100 : 0;

                    return (
                      <div key={index} className="progress-item">
                        <div className="progress-info">
                          <span>{agent.name}</span>
                          <span>{agent.calls} Conversions</span>
                        </div>
                        <div className="progress-bar-bg">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${progressPercent}%`, backgroundColor: 'var(--primary)' }}
                          ></div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="empty-chart">No converted leads in this period.</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
